/**
 * @fileoverview Clickable chart-card header strip linking to a drill-down page.
 * @module shared/components/ui/ChartCardHeader
 */

import { ReactElement, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ExpandIcon } from './ExpandIcon'
import { cn } from '@/shared/utils/cn'

export interface ChartCardHeaderProps {
  /** Header title text */
  title: string
  /** Drill-down destination — the whole header strip is the link */
  href: string
  /** Optional node rendered between the title and the expand icon (e.g. a stat) */
  trailing?: ReactNode
  /** Additional classes */
  className?: string
}

/**
 * Chart card header where the entire strip navigates to a drill-down view.
 * Hover brightens the strip and nudges the expand icon toward the top-right.
 *
 * @component
 * @example
 * ```tsx
 * <ChartCardHeader title="Costs" href="/dashboard/performance?sort=sales:desc" />
 * ```
 */
export function ChartCardHeader({
  title,
  href,
  trailing,
  className,
}: ChartCardHeaderProps): ReactElement {
  return (
    <Link
      to={href}
      className={cn(
        'group flex items-center justify-between px-6 pt-5 pb-4 cursor-pointer transition-colors duration-200 hover:bg-white/5',
        className
      )}
    >
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground transition-colors group-hover:text-foreground">
        {title}
      </h3>
      <div className="flex items-center gap-3">
        {trailing}
        <ExpandIcon />
      </div>
    </Link>
  )
}
